import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Loader from '../../components/common/Loader';
import SubscriptionService from '../../services/subscription.service';

const GoogleCallback: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('processing');
  const location = useLocation();
  const navigate = useNavigate();
  const { setAuthState } = useAuth();
  
  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the authorization code and plan info from URL
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        
        // Get plan info from sessionStorage (set in SocialLoginButtons)
        const planType = sessionStorage.getItem('planType') || 'basic';
        const planDuration = sessionStorage.getItem('planDuration') || 'monthly';
        
        // Clear session storage
        sessionStorage.removeItem('planType');
        sessionStorage.removeItem('planDuration');
        
        if (!code) {
          throw new Error('No authorization code found');
        }
        
        // Send the code to your backend
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google/exchange`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code }),
        });
        
        if (!response.ok) {
          throw new Error('Failed to exchange code for token');
        }
        
        const data = await response.json();
        
        // Store the token
        localStorage.setItem('token', data.accessToken);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Update auth context
        setAuthState({
          isAuthenticated: true,
          currentUser: data.user,
          loading: false,
          error: null
        });
        
        // Check if premium plan was selected
        if (planType !== 'basic') {
          setStatus('payment');
          
          // Initiate payment flow
          try {
            const paymentResponse = await SubscriptionService.upgrade(
              planType as 'premium' | 'ultimate',
              planDuration as 'monthly' | 'yearly'
            );
            
            if (paymentResponse.data && paymentResponse.data.authorizationUrl) {
              // Redirect to payment page
              window.location.href = paymentResponse.data.authorizationUrl;
              return; // Stop execution
            }
          } catch (paymentError) {
            console.error('Payment initiation failed:', paymentError);
            // Continue to dashboard even if payment fails
          }
        }
        
        // Navigate to dashboard for basic plan or if payment init fails
        navigate('/admin');
      } catch (err) {
        console.error('OAuth callback error:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
        setStatus('error');
      }
    };
    
    handleCallback();
  }, [location, navigate, setAuthState]);
  
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Authentication Error</h2>
          <p className="text-gray-700">{error}</p>
          <button 
            onClick={() => navigate('/login')}
            className="mt-6 w-full bg-pink-600 text-white py-2 px-4 rounded hover:bg-pink-700"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
      <div className="text-center">
        <Loader size="lg" />
        <p className="mt-4 text-lg text-gray-600">
          {status === 'payment' 
            ? 'Preparing your premium subscription...' 
            : 'Completing your Google login...'}
        </p>
      </div>
    </div>
  );
};

export default GoogleCallback;